import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { sha1 } from "object-hash";
import { useDebouncedCallback } from "use-debounce/lib";
import { cloneDeep } from "lodash";

import { useTypedSelector } from "../../../../../redux/store";
import { Map as MapService } from "../../../../../services";
import { ValueLanguage } from "../../../../../assets/languages/langs";

export interface UseMapSearchStreet {
	response: MapService.Search.Response["objects"];
	loading: boolean;
	get: (data: MapService.Search.StreetOptions) => void;
	updateOptions: (data: MapService.Search.StreetOptions) => void;
	reset: () => void;
}

export type UseMapSearchStreetProps = () => UseMapSearchStreet;

/**
 *  `useMapSearchStreet`
 */
export const useMapSearchStreet: UseMapSearchStreetProps = () => {
	const language = useTypedSelector((state) => state.session.language);
	const {
		inputRequestDelayMs: delaySearchRequest,
		searchRadiusMeters,
		minSearchQueryLength,
	} = useTypedSelector((state) => state.settings.map);

	const [options, setOptions] = useState<
		MapService.Search.StreetOptions | undefined
	>(undefined);

	const [favorite, setFavorite] = useState<
		MapService.Search.Response["objects"]
	>([]);

	const [addresses, setAddresses] = useState<
		MapService.Search.Response["objects"]
	>([]);

	const [streets, setStreets] = useState<
		MapService.Search.Response["objects"]
	>([]);

	const [localObject, setLocalObject] = useState<
		MapService.Search.Response["objects"]
	>([]);

	const [localObjectGrouped, setLocalObjectGrouped] = useState<
		MapService.Search.Response["objects"]
	>([]);

	const [settlement, setSettlement] = useState<
		MapService.Search.Response["objects"]
	>([]);

	const loadingRef = useRef<boolean>(true);
	const keyRef = useRef<string | null>(null);
	const rowRef = useRef<string | number | null>(null);
	const orderIdRef = useRef<number | null>(null);
	const languageRef = useRef<ValueLanguage>(language);

	const minSearchQueryLengthRef = useRef(minSearchQueryLength);
	const searchRadiusRef = useRef(minSearchQueryLength);
	const subscriptionRef = useRef<ReturnType<
		typeof MapService.searchStreet
	> | null>(null);

	minSearchQueryLengthRef.current = minSearchQueryLength;
	searchRadiusRef.current = searchRadiusMeters;
	languageRef.current = language;

	const callback = useCallback((res: MapService.Search.StreetResponse) => {
		if (!res.key) return;

		// Не режем потік: порівнюємо row/orderId лише коли є ОБИДВА значення.
		// Інакше `1703 !== undefined` або `null !== undefined` глушило б усі emits
		// (у т.ч.type=addresses коли redis/gateway частково їх віддає підписці).
		if (
			rowRef.current != null &&
			res.row != null &&
			rowRef.current !== res.row &&
			String(rowRef.current) !== String(res.row)
		)
			return;
		if (
			orderIdRef.current != null &&
			res.orderId != null &&
			orderIdRef.current !== res.orderId
		)
			return;

		loadingRef.current = false;

		if (res.type === "key") {
			keyRef.current = res.key;
			return;
		}
		if (keyRef.current !== res.key) return;

		const { data } = res;

		if (!Array.isArray(data)) return;
		if (!data.length) return;

		const lang = languageRef.current;

		const listType: MapService.Search.MapSearchStreetType[] = [
			"localObject",
			"localObjectGrouped",
			"settlement",
			"addresses",
			"streets",
		];

		if (res.type === "favorite") {
			const streets = data as MapService.Search.Object[];
			const newValue = streets.map((item) =>
				MapService.normalizeItem(item, lang),
			);
			setFavorite(newValue);
		} else if (listType.includes(res.type)) {
			const streets = data as MapService.Search.Object[];
			const newValue = streets.map((item) =>
				MapService.normalizeItem(item, lang),
			);

			if (res.type === "addresses") setAddresses(newValue);
			if (res.type === "streets") setStreets(newValue);
			if (res.type === "localObject") setLocalObject(newValue);
			if (res.type === "localObjectGrouped")
				setLocalObjectGrouped(newValue);
			if (res.type === "settlement") setSettlement(newValue);
		} else if (res.type === "houses") {
			const housesMap: Map<
				MapService.Search.SearchStreetHouses["id"],
				MapService.Search.SearchStreetHouses["streetHouses"]
			> = new Map();
			const houses = data as MapService.Search.SearchStreetHouses[];
			for (const house of houses) {
				if (house?.streetHouses?.length) {
					housesMap.set(house.id, house?.streetHouses);
				}
			}

			if (housesMap.size) {
				setStreets((prev) => {
					if (!prev) return prev;
					const items = prev.map((item) => {
						if (item?.id && housesMap.has(item.id)) {
							const streetHouses = cloneDeep(
								housesMap.get(item.id),
							);
							if (streetHouses)
								return { ...item, houses: streetHouses };
						}
						return item;
					});
					housesMap.clear();
					return items;
				});
			}
		}
	}, []);

	const get = useCallback<UseMapSearchStreet["get"]>(
		async (opt) => {
			loadingRef.current = true;
			// Одна подписка на весь жизненный цикл хука: каждый вызов
			// searchStreet создавал НОВУЮ серверную подписку (SubscriptionPool.add
			// не дедуплицирует), они копились за каждый ввод и фанаутили события
			// N раз — поиск деградировал до перезагрузки страницы.
			if (!subscriptionRef.current) {
				subscriptionRef.current = MapService.searchStreet(callback);
			}
			try {
				const sub = await subscriptionRef.current;
				await sub?.update?.(opt);
			} catch (error) {
				subscriptionRef.current = null;
				throw error;
			} finally {
				loadingRef.current = false;
			}
		},
		[callback],
	);

	useEffect(
		() => () => {
			const pending = subscriptionRef.current;
			subscriptionRef.current = null;
			pending?.then((sub) => sub?.unsubscribe?.()).catch(() => undefined);
		},
		[],
	);

	const updateOptions = useCallback<UseMapSearchStreet["updateOptions"]>(
		(opt) => {
			const minSearchQueryLength = minSearchQueryLengthRef.current;
			if (opt.query.length >= minSearchQueryLength) {
				keyRef.current = null;

				const searchRadius = searchRadiusRef.current;
				const clonedOptions = cloneDeep(opt);
				if (clonedOptions?.near) {
					clonedOptions.near.radius = searchRadius;
				}

				if (
					clonedOptions.row !== undefined &&
					clonedOptions.row !== null
				) {
					rowRef.current = clonedOptions.row as string | number;
				}
				orderIdRef.current = clonedOptions.orderId ?? null;

				if (clonedOptions?.searchType) {
					const searchType = Array.isArray(clonedOptions.searchType)
						? clonedOptions.searchType
						: [clonedOptions.searchType];

					if (!searchType.includes("favorite")) {
						setFavorite([]);
					}

					if (!searchType.includes("street")) {
						setAddresses([]);
						setStreets([]);
					}

					if (!searchType.includes("localObject")) {
						setLocalObject([]);
						setLocalObjectGrouped([]);
					}

					if (!searchType.includes("settlement")) {
						setSettlement([]);
					}
				}

				setOptions(clonedOptions);
			}
		},
		[],
	);

	const optionsHash = useMemo(
		() => (options ? sha1(options) : ""),
		[options],
	);
	const debounceGet = useDebouncedCallback(get, delaySearchRequest);

	useEffect(() => {
		if (options) debounceGet(options);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [optionsHash]);

	const resetBase = useCallback(() => {
		setFavorite([]);
		setAddresses([]);
		setStreets([]);
		setLocalObject([]);
		setLocalObjectGrouped([]);
		setSettlement([]);
		setOptions(undefined);
		keyRef.current = null;
		rowRef.current = null;
		orderIdRef.current = null;
		loadingRef.current = true;
	}, []);

	const reset = useDebouncedCallback(resetBase, 20);

	const response = useMemo(
		() => [
			...favorite,
			...addresses,
			...streets,
			...localObject,
			...localObjectGrouped,
			...settlement,
		],
		[
			addresses,
			favorite,
			localObject,
			localObjectGrouped,
			settlement,
			streets,
		],
	);

	return useMemo<UseMapSearchStreet>(
		() => ({
			response,
			loading: loadingRef?.current,
			reset,
			updateOptions,
			get,
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[response, loadingRef?.current, reset, updateOptions, get],
	);
};
